import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * WebGPU adapter capability matrix (novel_bot_detection §2E).
 *
 * WebGPU maps directly to Vulkan / Metal / DirectX 12 and gives a second,
 * independent view of the GPU/OS layer that legacy WebGL shims often leave
 * untouched. Reading adapter.limits and adapter.info is a useful coherence
 * target against a spoofed User-Agent, but it is not a tamper-proof read and
 * this project makes no claim that anti-detect browsers "rarely fake WebGPU".
 * Browsers themselves bucket several limits (maxTextureDimension2D is commonly
 * rounded to a power-of-two tier, for example), so identical limit sets mean a
 * class of device, not identical hardware.
 *
 * We request an adapter but never submit work, so this stays cheap and safe.
 */
export const webgpuProbe: Probe = {
  id: 'webgpu',
  label: 'WebGPU',
  layer: 'gpu',
  tier: 'client',
  category: 'hardware',
  description: 'GPU adapter info, limits, and features via WebGPU',
  budgetMs: 4000,

  async run(): Promise<ProbeResult> {
    const gpu = (navigator as Navigator & { gpu?: GPULike }).gpu;
    if (!gpu?.requestAdapter) {
      return {
        signals: {
          available: na<boolean>('unsupported', 'client', 'navigator.gpu unavailable'),
        },
      };
    }

    let adapter: GPUAdapterLike | null = null;
    let reason: string | null = null;
    try {
      adapter = await gpu.requestAdapter({ powerPreference: 'low-power' });
      if (!adapter) {
        // Fall back without a preference, then report why.
        try {
          adapter = await gpu.requestAdapter();
        } catch (e) {
          reason = String((e as Error)?.message ?? e);
        }
      }
    } catch (e) {
      reason = String((e as Error)?.message ?? e);
    }

    if (!adapter) {
      return {
        signals: {
          available: ok(false, 'client'),
          adapterReason: ok(reason ?? 'requestAdapter returned null', 'client'),
        },
      };
    }

    // adapter.info is the modern (spec-stable) surface; requestAdapterInfo was
    // the older Chromium-only name.
    let info: Record<string, unknown> | null = null;
    try {
      const withInfo = adapter as GPUAdapterLike & {
        info?: GPUAdapterInfoLike;
        requestAdapterInfo?: () => Promise<GPUAdapterInfoLike>;
      };
      if (withInfo.info) {
        info = { ...withInfo.info };
      } else if (withInfo.requestAdapterInfo) {
        info = { ...(await withInfo.requestAdapterInfo()) };
      }
    } catch {
      info = null;
    }

    // Full limits dump. Treat these as buckets, not a raw device read:
    // browsers cap and round many values (maxTextureDimension2D to a
    // power-of-two tier, for instance), so the set identifies a device class
    // rather than an exact GPU.
    let limits: Record<string, number> = {};
    try {
      const l = adapter.limits as unknown as Record<string, number>;
      limits = { ...l };
    } catch {
      limits = {};
    }

    const features: string[] = [];
    try {
      for (const f of adapter.features ?? []) features.push(String(f));
      features.sort();
    } catch {
      /* leave empty */
    }

    // A few high-signal limits surfaced individually for quick reading.
    const keyLimits = {
      maxTextureDimension2D: limits['maxTextureDimension2D'] ?? null,
      maxStorageBufferBindingSize: limits['maxStorageBufferBindingSize'] ?? null,
      maxComputeInvocationsPerWorkgroup: limits['maxComputeInvocationsPerWorkgroup'] ?? null,
      maxBufferSize: limits['maxBufferSize'] ?? null,
      maxUniformBufferBindingSize: limits['maxUniformBufferBindingSize'] ?? null,
    };

    const architecture = (info?.architecture as string) ?? null;
    const vendor = (info?.vendor as string) ?? null;
    const description = (info?.description as string) ?? null;

    return {
      signals: {
        available: ok(true, 'client'),
        vendor: ok(vendor, 'client'),
        architecture: ok(architecture, 'client'),
        device: ok((info?.device as string) ?? null, 'client'),
        description: ok(description, 'client'),
        // adapter.info.architecture is the key coherence field (e.g. 'apple-m1').
        keyLimits: ok(keyLimits, 'client'),
        limitCount: ok(Object.keys(limits).length, 'client'),
        features: ok(features, 'client'),
      },
      parts: {
        webgpu: {
          // Hash the high-signal limit values + feature set. The description
          // string contains driver versions, so it is excluded from the part.
          keyLimits,
          features,
          architecture,
        },
      },
    };
  },
};

interface GPULike {
  requestAdapter: (opts?: { powerPreference?: string }) => Promise<GPUAdapterLike | null>;
}

interface GPUAdapterLike {
  limits: Record<string, number>;
  features?: Iterable<string>;
  info?: GPUAdapterInfoLike;
  requestAdapterInfo?: () => Promise<GPUAdapterInfoLike>;
}

interface GPUAdapterInfoLike {
  vendor?: string;
  architecture?: string;
  device?: string;
  description?: string;
}

registerProbe(webgpuProbe);
